import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from "@angular/common/http";
import {Config, ConfigService} from "../../config";
import {LicenseResponseDto} from "../../dto/license-response-dto";
import {VehicleRequestDto} from "../../dto/vehicle-request-dto";
import {VehicleResponseDto} from "../../dto/vehicle-response-dto";
import {Context} from "../../models/context";
import {AnalysisResponseDto} from "../../dto/analysis-response-dto";
import {Utils} from "../../../shared/utils/generics.utils";
import {firstValueFrom, Observable} from "rxjs";
import {GetAnalysisResponseDto} from "../../dto/get-analysis-response-dto";
import {AnalysisStatusEnum} from "../../models/analysis-status";
import {UpdateResponseAnalysisDto} from "../../dto/update-response-analysis-dto";
import {TyreAnalysisRequestDto} from "../../dto/tyre-analysis-request-dto";
import {TyreAnalysisResponseDto} from "../../dto/tyre-analysis-response-dto";
import {RemindersRequestDto} from "../../dto/reminders-request-dto";
import {ReportResponseDto} from "../../dto/report-response-dto";
import {RecommendationsResponseDto} from "../../dto/recommendations-response-dto";
import {PredictionsResponseDto} from "../../dto/predictions-response-dto";
import {MatomoResponseDto} from "../../dto/matomo-response-dto";
import {VehicleInfoResponseDto} from "../../dto/vehicle-info-response-dto";
import {AnalysisPostcode} from "../../models/analysis";
import {CbdDto} from "../../dto/cbd-dto";
import {PatchAnalysisResponseDto} from "../../dto/patch-analysis-response-dto";
import {PostContactResponseDto} from "../../dto/post-contact-response-dto";
import {VehiclesAnalysesResponse} from "../../dto/vehicles-analyses-response";
import {SpareTyreResultEnum} from "../../models/spare-tyre-result-enum";

@Injectable({
  providedIn: 'root'
})
export class ReferenceApiService {

  private http = inject(HttpClient);
  private configService = inject(ConfigService);
  private config = this.configService.getConfig().rool

  setConfig(config: Config) {
    this.config = config.rool;
  }

  ///////////////// Vehicle //////////////////

  getVehicleIdByLicensePlate(licensePlate: string, licenseCountryCode: string) {
    const params = new HttpParams().set("licenseCountry", licenseCountryCode);
    return this.http.get<LicenseResponseDto>(
      `${this.config?.referenceApi.url}/licenses/${licensePlate}`, {params}
    );
  }

  createVehicle(vehicle: VehicleRequestDto) {
    return this.http.post<string>(
      `${this.config?.referenceApi.url}/vehicles`,
      vehicle, {observe: 'response'}
    )
  }

  getVehicleInfo(licensePlate: string, licenseCountry: string) {
    return this.http.get<VehicleInfoResponseDto>(`${this.config?.referenceApi.url}/vehicles/${licensePlate}/info`,
      { params: new HttpParams({ fromObject: { licenseCountry }})}
    );
  }

  getVehicle(vehicleId: number) {
    return this.http.get<VehicleResponseDto>(`${this.config?.referenceApi.url}/vehicles/${vehicleId}`);
  }

  ///////////////// Contact //////////////////

  createContact(analysesId: number, email?: string, phone?: string) {
    return this.http.post<PostContactResponseDto>(
      `${this.config?.referenceApi.url}/analyses/${analysesId}/contacts`,
      {email, phone});
  }

  patchUser(userId: string, phone: string) {
    return this.http.patch<Response>(
      `${this.config?.referenceApi.url}/users/${userId}`,
      {phone}, { observe: 'response' });
  }

  patchOptin(consent: boolean, contactId: number, contextUUID: string) {
    return this.http.patch<Response>(`${this.config?.referenceApi.url}/contacts/${contactId}/opt-ins/${contextUUID}`, {consent}, {observe: 'response'});
  }

  ///////////////// Analysis //////////////////

  createAnalysis(vehicleId: number,context: Context, companyId: number, fleet: string, language: string, vehicleMileage?: number, externalId?: string) {
    return this.http.post<AnalysisResponseDto>(
      `${this.config?.referenceApi.url}/v3/vehicles/${vehicleId}/analyses`,
      {appOrigin: Utils.getAppOrigin(context),
        contextOrigin: fleet, language,
        vehicleMileage, companyId, externalId}
    )
  }

  getAnalysis(vehicleId: number, context: Context): Observable<GetAnalysisResponseDto> {
    return this.http.get<GetAnalysisResponseDto>(
      `${this.config?.referenceApi.url}/v3/vehicles/${vehicleId}/analyses?appOrigin=${Utils.getAppOrigin(context)}`);
  }

  updateAnalysis(analysisId: number, status?: AnalysisStatusEnum, vehicleMileage?: number, postcode?: AnalysisPostcode) {
    let body;
    if (postcode?.codeCommune &&  postcode?.codePostal) {
      body = {status, vehicleMileage, inseeCodeTravelZone: postcode?.codeCommune, zipCodeTravelZone: postcode?.codePostal}
    } else {
      body = {status, vehicleMileage}
    }
    return this.http.patch<UpdateResponseAnalysisDto>(
      `${this.config?.referenceApi.url}/v3/analyses/${analysisId}`,
      body, { observe: 'response' }
    )
  }

  createTyreAnalysis(analysisId: number, analysis: TyreAnalysisRequestDto) {
    return this.http.post<TyreAnalysisResponseDto>(
      `${this.config?.referenceApi.url}/v3/analyses/${analysisId}/tyre-analyses`,
      analysis
    )
  }

  updateTyreAnalysis(tyreAnalyseId: number, status: AnalysisStatusEnum) {
    return this.http.patch<Response>(
      `${this.config?.referenceApi.url}/v3/tyre-analyses/${tyreAnalyseId}`,
      {status}, { observe: 'response' }
    )
  }

  createSpareTyreAnalysis(analysisId: number) {
    return firstValueFrom(this.createSpareTyreAnalysis$(analysisId));
  }

  createSpareTyreAnalysis$(analysisId: number) {
    return this.createVehicleAnalysis(analysisId, 'SPARE_TYRE');
  }

  updateSpareTyreAnalysis(vehicleAnalysisId: number, spareTyreResult: SpareTyreResultEnum) {
    return firstValueFrom(this.updateSpareTyreAnalysis$(vehicleAnalysisId, spareTyreResult));
  }

  updateSpareTyreAnalysis$(vehicleAnalysisId: number, spareTyreResult: SpareTyreResultEnum) {
    return this.http.post<Response>(
      `${this.config?.referenceApi.url}/vehicle-analyses/${vehicleAnalysisId}/spare-tyre-result`,
      {spareTyreResult}
    );
  }

  createVehicleAnalysis(analysisId: number, analysisType: string, pictureQuantity?: number) {
    return this.http.post<VehiclesAnalysesResponse>(
      `${this.config?.referenceApi.url}/analyses/${analysisId}/vehicle-analyses`,
      {analysisType, pictureQuantity}
    );
  }

  updateVehicleAnalysis(vehicleAnalysisId: number, status: AnalysisStatusEnum) {
    return firstValueFrom(this.updateVehicleAnalysis$(vehicleAnalysisId, status));
  }

  updateVehicleAnalysis$(vehicleAnalysisId: number, status: AnalysisStatusEnum) {
    return this.http.patch<Response>(
      `${this.config?.referenceApi.url}/vehicle-analyses/${vehicleAnalysisId}`,
      {status}
    );
  }

  ///////////////// Reminder //////////////////

  createReminder(dto: RemindersRequestDto) {
    return this.http.post<Response>(`${this.config?.referenceApi.url}/reminders`, {...dto});
  }

  ///////////////// RECOMMENDATIONS //////////////////

  getRecommendations(analysisId: number) {
    return this.http.get<RecommendationsResponseDto>(`${this.config?.referenceApi.url}/v3/analyses/${analysisId}/recommendations`);
  }

  getPredictions(analysisId: number) {
    return this.http.get<PredictionsResponseDto>(`${this.config?.referenceApi.url}/v3/analyses/${analysisId}/predictions`)
  }

  ///////////////// MATOMO //////////////////

  getMatomoId(companyId: number) {
    return this.http.get<MatomoResponseDto>(`${this.config?.referenceApi.url}/companies/${companyId}/matomo-sites`, { observe: 'response' });
  }

  ///////////////// CBD //////////////////

  createCBDWear(tyreAnalysisId: number) {
    return this.http.post<CbdDto>(`${this.config?.referenceApi.url}/v3/tyre-analyses/${tyreAnalysisId}/cbd-wear`, {});
  }

  patchCBDWear(cbdWearId: number, status: AnalysisStatusEnum) {
    return this.http.patch<PatchAnalysisResponseDto>(`${this.config?.referenceApi.url}/cbd-wear/${cbdWearId}`, {status});
  }

  createCBDRef(tyreAnalysisId: number) {
    return this.http.post<CbdDto>(`${this.config?.referenceApi.url}/v3/tyre-analyses/${tyreAnalysisId}/cbd-reference`, {});
  }

  patchCBDRef(cbdRefId: number, status: AnalysisStatusEnum) {
    return this.http.patch<PatchAnalysisResponseDto>(`${this.config?.referenceApi.url}/cbd-reference/${cbdRefId}`, {status});
  }

  /******************/
  /****  Report  ****/
  /******************/

  getReportForVehicle(companyId: number, license: string, licenseCountryCode: string) {
    const params = new HttpParams().set("licenseCountry", licenseCountryCode);
    return this.http.get<ReportResponseDto>(`${this.config?.referenceApi.url}/companies/${companyId}/licenses/${license}/reports/last`, {params})
  }

  getReportFromContext(contactId: string, contextUuid: string) {
    return this.http.get<ReportResponseDto>(`${this.config?.referenceApi.url}/contacts/${contactId}/opt-ins/${contextUuid}/reports`);
  }
}
